import { Component, OnDestroy, OnInit } from '@angular/core';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { OrganizationsService } from '../../../organization/components/service/organizations.service';
import { CompanyBackendService } from '../../../pp/service/company-backend.service';
import { AuthService } from '../../../auth/service/auth.service';
import { getKpiCompanyMetricsUrl, KPI_URLS } from '../../module/kpi-module-routing.module';
import { Company } from '../../../pp/dto/Company';
import { MetricState } from '../../dto/MetricState';
import { MetricComponentStateService } from '../../service/internal/metric-component-state.service';
import { UUID } from '../../../../core/pojo/id-variable';
import { Organization } from '../../../organization/dto/Organization';
import { fadeInOutAfterFirstAnimation, fadeInOutAnimation } from '../../../../util/animations/fade-in-out.animation';
import { CompanyWithMetricsDTO } from 'src/app/domain/company/dto/CompanyWithMetricsDTO';
import { COMPANY_CRUD_ACTION, CompanyFromCrudModalWrapper } from "../../../company/dto/CompanyAuxiliary";

@Component({
  selector: 'app-kpi-metrics',
  templateUrl: './kpi-metrics.component.html',
  styleUrls: ['./kpi-metrics.component.scss'],
  animations: [fadeInOutAnimation(), fadeInOutAfterFirstAnimation()],
})
export class KpiMetricsComponent implements OnInit, OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();

  selectedCompany: CompanyWithMetricsDTO;
  selectedCompanyForCrudModal: Company;
  selectedOrg: Organization;
  is = new Is();
  tabs = new Tabs();
  temp = new Temp();
  showCompanyCrud = false;

  companies: CompanyWithMetricsDTO[];
  metricState: MetricState = MetricState.noActionSelected;

  constructor(
    private orgService: OrganizationsService,
    private companiesService: CompanyBackendService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private metricStateService: MetricComponentStateService) { }

  ngOnInit(): void {
    this.initSubscriptions();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  async closeCompanyCrudModal(isSuccessful: boolean): Promise<void> {
    if (isSuccessful) {
      await this.initCompanies();
    }
    this.showCompanyCrud = false;
  }

  async goToCompany(companyUUID: UUID): Promise<void> {
    const company = this.companies.find((c) => c.uuid === companyUUID);
    if (!company) {
      return;
    }
    this.tabs.overview.selected = OverviewTab.metrics;
    const url = getKpiCompanyMetricsUrl(company.uuid);
    await this.router.navigate([url], { queryParams: this.route.snapshot.queryParams });
  }

  async updateCompanyList(wrapper: CompanyFromCrudModalWrapper): Promise<void> {
    this.temp.companyUUID = this.selectedCompany.uuid;
    await this.initCompanies();
    if (wrapper.action !== COMPANY_CRUD_ACTION.DELETE) {
      this.selectedCompany = this.companies.find((company) => company.uuid === this.selectedCompany.uuid);
    } else {
      await this.initSelectedCompany();
    }
  }

  async showCompanyCRUDModal(): Promise<void> {
    if (this.selectedCompany) {
      this.selectedCompanyForCrudModal = await firstValueFrom(this.companiesService
        .getByUUID(this.selectedCompany.uuid).pipe(takeUntil(this._destroyStream)));
    }
    this.showCompanyCrud = true;
  }

  private async initCompanies(): Promise<void> {
    this.companies = await firstValueFrom(this.getCompanies().pipe(takeUntil(this._destroyStream)));
    if (!this.companies.length) {
      this.selectedCompany = null;
      return;
    }
    if (this.selectedCompany && this.selectedCompany.uuid) {
      await this.goToCompany(this.selectedCompany.uuid);
      return;
    }
    await this.goToCompany(this.companies[0].uuid);
  }

  private async initSelectedCompany(): Promise<void> {
    // company from url
    let company = this.companies.find((c) => c.uuid === this.temp.companyUUID);
    company ??= (this.companies.length ? this.companies[0] : null); // first company in the list
    this.temp.companyUUID = null;
    if (company === this.selectedCompany) {
      return;
    }
    await this.goToCompany(company.uuid);
    this.selectedCompany = company;
    this.metricStateService.setSelectedCompany(company);
  }

  private initSubscriptions(): void {
    this.orgService.selected$
      .pipe(
        filter((org) => Boolean(org)),
        tap((org) => this.selectedOrg = org),
        switchMap(() => this.getCompanies()),
        tap((companies) => {
          if (!companies) {
            return;
          }
          this.companies = companies;
          this.selectedCompany = null;
        }),
        filter((companies) => Boolean(companies)),
        tap(() => this.parseCompanyFromURL()),
        switchMap(() => this.metricStateService.getSelectedCompany),
        tap((selectedCompany) => {
          if (selectedCompany && !this.temp.companyUUID) {
            this.temp.companyUUID = selectedCompany.uuid;
          }
        }),
        switchMap(() => this.initSelectedCompany()),
        switchMap(() => this.router.events),
        filter((event: NavigationEnd) => event instanceof NavigationEnd),
        tap(() => this.parseCompanyFromURL()),
        switchMap(() => this.initSelectedCompany()),
        takeUntil(this._destroyStream))
      .subscribe();

    this.authService.currentUserTypeForServices$.pipe(takeUntil(this._destroyStream)).subscribe((userType) => {
      this.is.admin = userType === 'admin';
    });

    this.metricStateService.getMetricState
      .pipe(takeUntil(this._destroyStream))
      .subscribe((state) => {
        this.metricState = state;
      });
  }

  private getCompanies(): Observable<CompanyWithMetricsDTO[]> {
    return this.companiesService.getWithMetricsByOrg(this.selectedOrg.uuid);
  }

  private parseCompanyFromURL(): void {
    const url = this.router.url;
    if (!url.startsWith(getKpiCompanyMetricsUrl())) {
      return;
    }
    this.temp.companyUUID = url.split(KPI_URLS._companyMetrics + '/')[1].split('?')[0];
  }
}

class Is {
  admin: boolean;
}

class Temp {
  companyUUID: string;
}

class Tabs {
  overview: OverviewTabWrapper = {
    all: [OverviewTab.metrics, OverviewTab.charts],
    selected: OverviewTab.metrics,
  };
}

interface OverviewTabWrapper {
  all: OverviewTab[];
  selected: OverviewTab;
}

enum OverviewTab {
  metrics = 'Metrics',
  charts = 'Charts',
}

